import { useState } from 'react'
import { updateReminderSettings, getRoutineReminders } from '../utils/routineStorage'
import type { RoutineBlock } from '../utils/routineStorage'

interface ReminderSettingsProps {
  routine: RoutineBlock
  onClose: () => void
  onSave: () => void
}

const ReminderSettings = ({ routine, onClose, onSave }: ReminderSettingsProps) => {
  const [enabled, setEnabled] = useState(routine.reminder_enabled || false)
  const [time, setTime] = useState(routine.reminder_time || '09:00')
  const [windowMinutes, setWindowMinutes] = useState(routine.reminder_window || 60)

  const handleSave = () => {
    updateReminderSettings(routine.id, enabled, time, windowMinutes)
    onSave()
  }

  const getTimeRangeLabel = () => {
    switch (routine.time_range) {
      case 'morning': return 'Morning (6 AM - 12 PM)'
      case 'afternoon': return 'Afternoon (12 PM - 6 PM)'
      case 'evening': return 'Evening (6 PM - 10 PM)'
      default: return 'Anytime'
    }
  }

  const getSuggestedTimes = () => {
    switch (routine.time_range) {
      case 'morning': return ['07:00', '08:00', '09:00', '10:00']
      case 'afternoon': return ['12:00', '14:00', '15:00', '16:00']
      case 'evening': return ['18:00', '19:00', '20:00', '21:00']
      default: return ['09:00', '12:00', '15:00', '18:00']
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 sm:p-8">
      <div className="bg-cream-25 rounded-3xl w-full max-w-md">
        {/* Header */}
        <div className="border-b border-cream-100/50 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-cream-600 font-light text-base sm:text-lg">
              Reminder Settings
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-cream-100/60 transition-colors focus:outline-none focus:ring-2 focus:ring-cream-100"
            >
              <svg className="w-5 h-5 text-cream-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <p className="text-cream-500 font-light text-xs sm:text-sm mt-2">
            {routine.name} • {getTimeRangeLabel()}
          </p>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-3 sm:p-4 bg-cream-50/60 rounded-2xl border border-cream-100/30">
            <div>
              <h3 className="text-cream-600 font-light text-xs sm:text-sm mb-1">
                Gentle reminders
              </h3>
              <p className="text-cream-500 font-light text-xs">
                Soft notifications when it's time for this routine
              </p>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              className={`relative inline-flex h-5 sm:h-6 w-10 sm:w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-cream-200 focus:ring-offset-2 ${
                enabled ? 'bg-sage-300' : 'bg-cream-200'
              }`}
            >
              <span
                className={`inline-block h-3 sm:h-4 w-3 sm:w-4 transform rounded-full bg-white transition-transform duration-300 ${
                  enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {enabled && (
            <>
              {/* Time Selection */}
              <div>
                <label className="block text-cream-600 font-light text-xs sm:text-sm mb-3">
                  Preferred time
                </label>
                
                {/* Quick time buttons */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {getSuggestedTimes().map((suggestedTime) => (
                    <button
                      key={suggestedTime}
                      onClick={() => setTime(suggestedTime)}
                      className={`p-2 sm:p-3 rounded-xl text-xs sm:text-sm font-light border transition-colors focus:outline-none focus:ring-2 focus:ring-cream-100 ${
                        time === suggestedTime
                          ? 'bg-cream-100 border-cream-200 text-cream-700'
                          : 'bg-white/50 border-cream-100/50 text-cream-600 hover:bg-cream-50'
                      }`}
                    >
                      {new Date(`2000-01-01T${suggestedTime}`).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </button>
                  ))}
                </div>
                
                {/* Custom time input */}
                <div>
                  <label className="block text-cream-500 font-light text-xs mb-2">
                    Or choose a custom time:
                  </label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full p-2 sm:p-3 rounded-xl border border-cream-200/50 focus:border-cream-300 focus:ring-2 focus:ring-cream-100 focus:outline-none font-light text-xs sm:text-sm bg-white/50"
                  />
                </div>
              </div>

              {/* Flexibility Window */}
              <div>
                <label className="block text-cream-600 font-light text-xs sm:text-sm mb-2 sm:mb-3">
                  Flexibility window
                </label>
                <p className="text-cream-500 font-light text-xs mb-2 sm:mb-3">
                  How much time before/after to show the reminder
                </p>
                
                <div className="space-y-2">
                  {[30, 60, 90, 120].map((minutes) => (
                    <button
                      key={minutes}
                      onClick={() => setWindowMinutes(minutes)}
                      className={`w-full p-2 sm:p-3 rounded-xl text-xs sm:text-sm font-light border transition-colors focus:outline-none focus:ring-2 focus:ring-cream-100 ${
                        windowMinutes === minutes
                          ? 'bg-cream-100 border-cream-200 text-cream-700'
                          : 'bg-white/50 border-cream-100/50 text-cream-600 hover:bg-cream-50'
                      }`}
                    >
                      ±{minutes} minutes
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="p-3 sm:p-4 bg-sage-50/40 rounded-2xl border border-sage-100/30">
                <h4 className="text-sage-600 font-light text-xs sm:text-sm mb-2">Preview</h4>
                <p className="text-sage-500 font-light text-xs leading-relaxed">
                  You'll get a gentle reminder between{' '}
                  {new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  }).replace(/:\d{2}/, (match) => {
                    const [hour, minute] = time.split(':').map(Number)
                    const startMinute = minute - windowMinutes
                    const endMinute = minute + windowMinutes
                    return `:${Math.max(0, startMinute).toString().padStart(2, '0')}`
                  })} and{' '}
                  {new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  }).replace(/:\d{2}/, (match) => {
                    const [hour, minute] = time.split(':').map(Number)
                    const endMinute = minute + windowMinutes
                    return `:${Math.min(59, endMinute).toString().padStart(2, '0')}`
                  })} for your "{routine.name}\" routine.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-cream-100/50 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-cream-50/60 hover:bg-cream-100/50 
                         text-cream-600 rounded-2xl text-xs sm:text-sm font-light border border-cream-100/40 hover:border-cream-200/50
                         transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-cream-100/50"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSave}
              className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-sage-100/80 hover:bg-sage-200/60 
                         text-sage-600 rounded-2xl text-xs sm:text-sm font-light border border-sage-200/50 hover:border-sage-300/60
                         transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReminderSettings